// Curado masivo de la Memoria: cuando alguien aprueba/rechaza/comparte varios hechos de una
// tanda, el backend colapsa esas filas en una y manda los textos en `detail.collapsed_items`
// (regla `memory_curation_burst` de core/services/activity_log_collapse.py).
//
// Una operación suelta NO trae `collapsed_items` — el detail queda vacío, `canRender` falla y
// la fila degrada a lo de siempre: título del backend y sin toggle "Ver detalle".

import React from 'react';
import {useTranslation} from 'react-i18next';
import {isPlainObject} from '../shapes';
import {isNumeric} from '../humanize';

const MAX_ITEMS_SHOWN = 6;

// Los textos de los hechos son contenido de usuario/LLM: van como TEXTO React, nunca por
// dangerouslySetInnerHTML.
function collapsedItems(detail) {
    if (!isPlainObject(detail) || !Array.isArray(detail.collapsed_items)) return [];
    return detail.collapsed_items.filter(item => typeof item === 'string' && item.trim());
}

export function canRenderMemoryCurationBurst(detail) {
    return collapsedItems(detail).length > 0;
}

export function memoryCurationBurstHasDetail(detail) {
    return canRenderMemoryCurationBurst(detail);
}

// Cuántos hechos abarca la fila. El contador autoritativo vive en el ENTRY (el backend lo cuenta
// antes de capar la lista); `collapsed_items` es sólo lo que se muestra.
function burstCount(entry, detail) {
    const occurrences = entry && entry.occurrences;
    if (isNumeric(occurrences) && Number(occurrences) > 1) return Number(occurrences);
    return collapsedItems(detail).length;
}

const TITLE_KEYS = {
    MEMORY_FACT_REJECTED: 'activity_log.title.memory_facts_rejected',
    MEMORY_FACT_APPROVED: 'activity_log.title.memory_facts_approved',
    MEMORY_FACT_SHARED: 'activity_log.title.memory_facts_shared',
    MEMORY_FACT_EDITED: 'activity_log.title.memory_facts_edited',
    MEMORY_FACT_CONFIRMED: 'activity_log.title.memory_facts_confirmed',
    MEMORY_FACT_AUTO_ARCHIVED: 'activity_log.title.memory_facts_archived',
    MEMORY_FACT_AUTO_MERGED: 'activity_log.title.memory_facts_auto_merged',
    MEMORY_FACT_MERGED: 'activity_log.title.memory_facts_merged',
    // Sin esta entrada, N restauraciones colapsadas tomaban el texto del ÚLTIMO hecho como
    // título de la fila, así que la Bitácora decía «Hecho rescatado del archivo: …» para una
    // tanda de cinco y no había forma de saber cuántos fueron (QA 2026-09-06, caso 20).
    MEMORY_FACT_RESTORED: 'activity_log.title.memory_facts_restored',
};

export function memoryCurationBurstTitle(entry, t) {
    if (!entry) return null;
    const key = TITLE_KEYS[entry.event_type];
    if (!key) return null;
    const count = burstCount(entry, entry.detail);
    if (count <= 1) return null;   // una sola operación conserva su título original
    return t(key, {count});
}

export function MemoryCurationBurstDetail({detail, entry}) {
    const {t} = useTranslation();
    const items = collapsedItems(detail);
    if (!items.length) return null;

    const shown = items.slice(0, MAX_ITEMS_SHOWN);
    const rest = burstCount(entry, detail) - shown.length;

    // Sin wrapper propio: `.ald-list` ya trae el estilo de las listas del detalle.
    return (
        <ul className="ald-list">
            {shown.map((item, index) => (
                <li key={index}><span className="ald-list-name">{item}</span></li>
            ))}
            {rest > 0 ? <li className="ald-list-more">{t('activity_log.detail.more_items', {count: rest})}</li> : null}
        </ul>
    );
}
